/**
 * useRecomputeProgress Hook
 * 
 * Provides real-time progress updates for scoring recompute operations
 * using Server-Sent Events (SSE)
 */

import { useState, useEffect } from 'react'

interface RecomputeProgress {
  job_id: string
  total: number
  current: number
  percentage: number
  message: string
  started_at?: string
  finished_at?: string
  finished?: boolean
  error?: string
}

interface UseRecomputeProgressReturn {
  progress: RecomputeProgress | null
  error: string | null
  isConnected: boolean
}

/**
 * Hook pentru tracking progres recompute scoring
 * 
 * @param jobId - UUID-ul job-ului de recompute (null = nu conectează)
 * @returns { progress, error, isConnected }
 * 
 * @example
 * ```tsx
 * const { progress, error, isConnected } = useRecomputeProgress(jobId)
 * 
 * if (progress && !progress.finished) {
 *   return <LinearProgress variant="determinate" value={progress.percentage} />
 * }
 * ```
 */
export const useRecomputeProgress = (jobId: string | null): UseRecomputeProgressReturn => {
  const [progress, setProgress] = useState<RecomputeProgress | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isConnected, setIsConnected] = useState<boolean>(false)

  useEffect(() => {
    // Nu conecta dacă nu avem job ID
    if (!jobId) {
      setProgress(null)
      setError(null)
      setIsConnected(false)
      return
    }

    let eventSource: EventSource | null = null

    try {
      // Construiește URL pentru SSE folosind current origin
      const apiUrl = window.location.origin
      const sseUrl = `${apiUrl}/api/v1/scoring/recompute-progress/${jobId}`

      // Creează EventSource pentru SSE
      eventSource = new EventSource(sseUrl, {
        withCredentials: true // Include cookies pentru auth
      })

      eventSource.onopen = () => {
        setIsConnected(true)
        setError(null)
      }

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as RecomputeProgress

          // Verifică dacă am primit eroare
          if (data.error) {
            setError(data.error)
            setIsConnected(false)
            eventSource?.close()
            return
          }

          // Update progress
          setProgress(data)

          // Închide conexiunea dacă job-ul s-a terminat
          if (data.finished) {
            setIsConnected(false)
            eventSource?.close()
          }
        } catch (err) {
          console.error('Error parsing SSE data:', err)
          setError('Error parsing progress data')
          eventSource?.close()
        }
      }

      eventSource.onerror = (err) => {
        console.error('SSE connection error:', err)
        setError('Connection lost to progress stream')
        setIsConnected(false)
        eventSource?.close()
      }
    } catch (err) {
      console.error('Error creating EventSource:', err)
      setError('Failed to connect to progress stream')
      setIsConnected(false)
    }

    // Cleanup on unmount or jobId change
    return () => {
      if (eventSource) {
        eventSource.close()
        setIsConnected(false)
      }
    }
  }, [jobId])

  return { progress, error, isConnected }
}

/**
 * Helper function pentru formatare progres mesaj
 */
export const formatProgressMessage = (progress: RecomputeProgress | null): string => {
  if (!progress) return ''
  
  if (progress.finished) {
    return 'Completed!'
  }
  
  return `${progress.message} (${progress.current}/${progress.total})`
}

/**
 * Helper function pentru formatare durată
 */
export const formatDuration = (started_at?: string, finished_at?: string): string => {
  if (!started_at) return ''
  
  const start = new Date(started_at)
  const end = finished_at ? new Date(finished_at) : new Date()
  
  const durationMs = end.getTime() - start.getTime()
  const seconds = Math.floor(durationMs / 1000)
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  
  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`
  }
  return `${seconds}s`
}

